import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Eraser, Hand, MoreHorizontal, Pencil, Plus, Redo2, Trash2, Undo2, X } from 'lucide-react';
import { can, type TeamSummary } from '@hoopify/shared';
import { api } from '../lib/api';
import * as G from '../lib/gameday';
import { CourtLines, viewBox } from '../components/diagram/Court';
import { FullScreenButton, useWakeLock } from './CourtsidePage';

/**
 * Game day: the phone or tablet becomes a court whiteboard. Draw with a finger
 * or stylus, rub out, move player markers, and wipe it for the next play.
 * Works without signal once open; boards are kept on this device.
 */

const INK: Record<'dark' | 'light', Record<G.InkColour, string>> = {
  dark: { ink: '#F6F4EF', pink: '#FF2E93', blue: '#00D1FF', orange: '#FF5A3C' },
  light: { ink: '#0B0B14', pink: '#D1006A', blue: '#006BA6', orange: '#D9540B' },
};
const COLOUR_NAMES: Record<G.InkColour, string> = { ink: 'White', pink: 'Pink', blue: 'Blue', orange: 'Orange' };

type Drag = { kind: 'stroke'; pointerId: number; stroke: G.Stroke } | { kind: 'marker'; pointerId: number; id: string; at: G.Pt; before: G.Board } | null;

export function GameDayPage() {
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  const isCoach = !teams.data || !teams.data.length || teams.data.some((t) => can(t.role, 'sessions:details'));
  if (!isCoach) {
    return (
      <div data-theme="dark" className="grid min-h-dvh place-items-center bg-paper p-6 text-center text-ink">
        <div>
          <p className="mb-4 text-[18px]">Game day is for coaches.</p>
          <Link to="/" className="btn-secondary">
            Back to the schedule
          </Link>
        </div>
      </div>
    );
  }
  return <Board />;
}

function Board() {
  useWakeLock();
  const navigate = useNavigate();
  const [game, setGame] = useState<G.GameDay>(G.loadGameDay);
  const [tool, setTool] = useState<G.Tool>('pen');
  const [colour, setColour] = useState<G.InkColour>('pink');
  const [thick, setThick] = useState(false);
  const [palette, setPalette] = useState(false);
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [undoClear, setUndoClear] = useState<number | null>(null);
  const [penSeen, setPenSeen] = useState(false);
  const historyRef = useRef(new Map<string, G.History>());
  const [, bump] = useState(0);

  const board = game.boards.find((b) => b.id === game.active) ?? game.boards[0]!;
  const theme = game.light ? 'light' : 'dark';
  const history = historyRef.current.get(board.id) ?? { past: [], future: [] };

  // Keep it on the device (debounced), and once more when the page is hidden.
  const gameRef = useRef(game);
  gameRef.current = game;
  useEffect(() => {
    const t = window.setTimeout(() => G.saveGameDay(game), 300);
    return () => window.clearTimeout(t);
  }, [game]);
  useEffect(() => {
    const save = () => G.saveGameDay(gameRef.current);
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', save);
    return () => {
      window.removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', save);
    };
  }, []);
  // No pull-to-refresh or bounce while drawing.
  useEffect(() => {
    const prev = document.body.style.overscrollBehavior;
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overscrollBehavior = prev;
    };
  }, []);

  const setBoard = useCallback((next: G.Board, recordFrom?: G.Board) => {
    if (recordFrom) {
      const h = historyRef.current.get(recordFrom.id) ?? { past: [], future: [] };
      historyRef.current.set(recordFrom.id, G.record(h, recordFrom));
    }
    setGame((g) => ({ ...g, boards: g.boards.map((b) => (b.id === next.id ? next : b)) }));
  }, []);

  const doUndo = () => {
    const r = G.undo(history, board);
    if (!r) return;
    historyRef.current.set(board.id, r.h);
    setBoard(r.board);
    bump((n) => n + 1);
  };
  const doRedo = () => {
    const r = G.redo(history, board);
    if (!r) return;
    historyRef.current.set(board.id, r.h);
    setBoard(r.board);
    bump((n) => n + 1);
  };
  const clear = (all: boolean) => {
    if (G.isEmpty(board) || (!all && !board.strokes.length)) return;
    setBoard(all ? G.clearAll(board) : G.clearDrawing(board), board);
    setUndoClear(Date.now());
    navigator.vibrate?.(15);
  };
  useEffect(() => {
    if (!undoClear) return;
    const t = window.setTimeout(() => setUndoClear(null), 5000);
    return () => window.clearTimeout(t);
  }, [undoClear]);

  // ---------- Stage size and view ----------
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  const view = useMemo(() => G.fit(board.court, Math.max(1, size.w - 8), Math.max(1, size.h - 8)), [board.court, size]);
  const v = useMemo(() => ({ ...view, ox: view.ox + 4, oy: view.oy + 4 }), [view]);

  // ---------- Drawing ----------
  const drag = useRef<Drag>(null);
  const [dragMarker, setDragMarker] = useState<{ id: string; at: G.Pt } | null>(null);

  const paint = useCallback(
    (extra?: G.Stroke) => {
      const c = canvasRef.current;
      if (!c || !size.w) return;
      const dpr = window.devicePixelRatio || 1;
      if (c.width !== Math.round(size.w * dpr) || c.height !== Math.round(size.h * dpr)) {
        c.width = Math.round(size.w * dpr);
        c.height = Math.round(size.h * dpr);
      }
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // Keep ink on the court (and its margin).
      ctx.save();
      ctx.beginPath();
      ctx.rect(v.ox, v.oy, v.w, v.h);
      ctx.clip();
      for (const s of extra ? [...board.strokes, extra] : board.strokes) {
        ctx.globalCompositeOperation = s.erase ? 'destination-out' : 'source-over';
        ctx.strokeStyle = s.erase ? '#000' : INK[theme][s.colour];
        ctx.fillStyle = ctx.strokeStyle;
        ctx.lineWidth = Math.max(2, s.width * v.s);
        const pts = s.points.map((p) => G.toScreen(v, p));
        if (pts.length === 1) {
          ctx.beginPath();
          ctx.arc(pts[0]!.x, pts[0]!.y, ctx.lineWidth / 2, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }
        if (!pts.length) continue;
        ctx.beginPath();
        ctx.moveTo(pts[0]!.x, pts[0]!.y);
        // Smooth through midpoints.
        for (let i = 1; i < pts.length - 1; i++) {
          const mx = (pts[i]!.x + pts[i + 1]!.x) / 2;
          const my = (pts[i]!.y + pts[i + 1]!.y) / 2;
          ctx.quadraticCurveTo(pts[i]!.x, pts[i]!.y, mx, my);
        }
        const last = pts[pts.length - 1]!;
        ctx.lineTo(last.x, last.y);
        ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.restore();
    },
    [board.strokes, size, theme, v],
  );
  useEffect(() => paint(), [paint]);

  const local = (e: { clientX: number; clientY: number }) => {
    const r = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const markerRadius = Math.max(14, Math.min(24, G.MARKER_R * v.s));
  const hitMarker = (q: G.Pt) => {
    let best: { id: string; d: number } | null = null;
    for (const m of board.markers) {
      const p = G.toScreen(v, m);
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d <= markerRadius + 8 && (!best || d < best.d)) best = { id: m.id, d };
    }
    return best?.id ?? null;
  };

  const onDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (drag.current) return; // one finger at a time
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (palette || menu) {
      setPalette(false);
      setMenu(false);
    }
    const isPen = e.pointerType === 'pen';
    if (isPen && !penSeen) setPenSeen(true);
    const q = local(e);
    const hit = hitMarker(q);
    // With a stylus around, fingers only move players (so a resting palm doesn't draw).
    const fingerMoves = penSeen && e.pointerType === 'touch';
    if (hit && (tool === 'move' || fingerMoves)) {
      e.currentTarget.setPointerCapture(e.pointerId);
      const m = board.markers.find((x) => x.id === hit)!;
      drag.current = { kind: 'marker', pointerId: e.pointerId, id: hit, at: { x: m.x, y: m.y }, before: board };
      setDragMarker({ id: hit, at: { x: m.x, y: m.y } });
      navigator.vibrate?.(8);
      return;
    }
    if (tool === 'move' || fingerMoves) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = G.toCourt(v, q);
    drag.current = {
      kind: 'stroke',
      pointerId: e.pointerId,
      stroke: { id: G.newId(), erase: tool === 'eraser', colour, width: tool === 'eraser' ? G.ERASER_WIDTH : thick ? G.WIDTHS.thick : G.WIDTHS.thin, points: [p] },
    };
    paint(drag.current.stroke);
  };

  const onMove = (e: RPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) {
      if (tool === 'eraser' && !d) setEraserAt(null);
      return;
    }
    const events = (e.nativeEvent.getCoalescedEvents?.() ?? []).length ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    if (d.kind === 'stroke') {
      let pts = d.stroke.points;
      for (const ev of events) pts = G.addPoint(pts, G.toCourt(v, local(ev)), 1.5 / v.s);
      d.stroke = { ...d.stroke, points: pts };
      paint(d.stroke);
      if (d.stroke.erase) setEraserAt(local(e));
    } else {
      d.at = G.toCourt(v, local(e));
      setDragMarker({ id: d.id, at: d.at });
    }
  };

  const onUp = (e: RPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    setEraserAt(null);
    if (d.kind === 'stroke') {
      setBoard({ ...board, strokes: [...board.strokes, d.stroke] }, board);
    } else {
      setDragMarker(null);
      const moved = G.moveMarker(board, d.id, d.at);
      const before = d.before.markers.find((m) => m.id === d.id)!;
      const after = moved.markers.find((m) => m.id === d.id)!;
      if (before.x !== after.x || before.y !== after.y) setBoard(moved, d.before);
    }
  };
  const onCancel = (e: RPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    setDragMarker(null);
    setEraserAt(null);
    paint();
  };
  const [eraserAt, setEraserAt] = useState<G.Pt | null>(null);

  // ---------- Boards ----------
  const addBoard = () => {
    if (game.boards.length >= G.MAX_BOARDS) return;
    const b = G.newBoard(`Board ${game.boards.length + 1}`, board.court);
    setGame((g) => ({ ...g, boards: [...g.boards, b], active: b.id }));
  };
  const removeBoard = () => {
    if (game.boards.length === 1) {
      setBoard(G.clearAll({ ...board, name: 'Board 1' }), board);
      return;
    }
    historyRef.current.delete(board.id);
    setGame((g) => {
      const boards = g.boards.filter((b) => b.id !== board.id);
      return { ...g, boards, active: boards[0]!.id };
    });
  };

  // ---------- Render ----------
  const across = (16.2) * v.s; // COURT_WIDTH + 2·PAD
  const along = (G.courtLen(board.court) + 2 * G.PAD) * v.s;
  const courtStyle: React.CSSProperties = v.rotated
    ? { left: v.ox, top: v.oy, width: across, height: along, transformOrigin: '0 0', transform: `translate(0px, ${across}px) rotate(-90deg)` }
    : { left: v.ox, top: v.oy, width: across, height: along };
  const markers = board.markers.map((m) => (dragMarker?.id === m.id ? { ...m, x: dragMarker.at.x, y: dragMarker.at.y } : m));
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  const toolBtn = (t: G.Tool, label: string, Icon: typeof Pencil) => (
    <button
      key={t}
      onClick={() => {
        setTool(t);
        setPalette(false);
      }}
      aria-pressed={tool === t}
      aria-label={label}
      title={label}
      className={`grid size-12 place-items-center rounded-lg ${tool === t ? 'bg-blue text-brand-ink' : 'text-ink hover:bg-white/10'}`}
    >
      <Icon className="size-6" strokeWidth={2} aria-hidden />
    </button>
  );

  return (
    <div data-theme="dark" className="fixed inset-0 flex flex-col bg-paper text-ink select-none [color-scheme:dark]" style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}>
      {/* Top bar */}
      <header className="flex h-12 shrink-0 items-center gap-1.5 border-b border-line px-2">
        <button onClick={() => navigate(-1)} className="grid size-10 place-items-center rounded-md text-muted hover:bg-white/10 hover:text-ink" aria-label="Leave game day">
          <ArrowLeft className="size-5" aria-hidden />
        </button>
        <nav className="flex min-w-0 flex-1 gap-1 overflow-x-auto" aria-label="Boards">
          {game.boards.map((b) => (
            <button
              key={b.id}
              onClick={() => setGame((g) => ({ ...g, active: b.id }))}
              aria-current={b.id === board.id ? 'true' : undefined}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-bold whitespace-nowrap ${b.id === board.id ? 'bg-sunken text-ink ring-1 ring-blue' : 'text-muted hover:text-ink'}`}
            >
              {b.name}
            </button>
          ))}
          {game.boards.length < G.MAX_BOARDS && (
            <button onClick={addBoard} className="grid size-8 shrink-0 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-ink" aria-label="New board">
              <Plus className="size-4" aria-hidden />
            </button>
          )}
        </nav>
        <div className="flex shrink-0 rounded-md bg-sunken p-0.5" role="radiogroup" aria-label="Court">
          {(['full', 'half'] as const).map((c) => (
            <button
              key={c}
              role="radio"
              aria-checked={board.court === c}
              onClick={() => board.court !== c && setBoard(G.setCourt(board, c), board)}
              className={`rounded px-2.5 py-1 text-[12px] font-bold tracking-wider uppercase ${board.court === c ? 'bg-blue text-brand-ink' : 'text-muted'}`}
            >
              {c}
            </button>
          ))}
        </div>
        <FullScreenButton />
        <button onClick={() => setMenu(!menu)} aria-expanded={menu} className="grid size-10 place-items-center rounded-md text-muted hover:bg-white/10 hover:text-ink" aria-label="Board options">
          <MoreHorizontal className="size-5" aria-hidden />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col landscape:flex-row">
        {/* Court */}
        <div
          ref={stageRef}
          data-theme={theme}
          className="relative min-h-0 flex-1 touch-none overflow-hidden bg-paper"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onCancel}
          onContextMenu={(e) => e.preventDefault()}
          role="application"
          aria-label={`${board.name}: ${board.court} court whiteboard`}
        >
          {size.w > 0 && (
            <svg viewBox={viewBox(board.court)} className="pointer-events-none absolute" style={courtStyle} aria-hidden>
              <CourtLines court={board.court} />
            </svg>
          )}
          <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
          {markers.map((m) => {
            const p = G.toScreen(v, m);
            const r = m.kind === 'ball' ? markerRadius * 0.62 : markerRadius;
            const lifted = dragMarker?.id === m.id;
            return (
              <div
                key={m.id}
                aria-hidden
                className="pointer-events-none absolute grid place-items-center rounded-full font-bold"
                style={{
                  left: p.x - r,
                  top: p.y - r,
                  width: r * 2,
                  height: r * 2,
                  fontSize: r * 0.95,
                  transform: lifted ? 'scale(1.25)' : undefined,
                  transition: 'transform 120ms cubic-bezier(.2,.8,.2,1)',
                  ...(m.kind === 'offence'
                    ? { background: 'var(--diagram-offence-fill)', border: `${Math.max(2, r * 0.16)}px solid var(--diagram-offence)`, color: 'var(--diagram-offence)' }
                    : m.kind === 'defence'
                      ? { color: 'var(--diagram-defence)', fontFamily: 'var(--font-display)', fontWeight: 900 }
                      : { background: 'var(--diagram-ball)', border: '2px solid var(--diagram-ball-line)' }),
                }}
              >
                {m.kind === 'defence' ? (
                  <span style={{ lineHeight: 1 }}>
                    X<sub style={{ fontSize: '0.55em' }}>{m.label}</sub>
                  </span>
                ) : (
                  m.label
                )}
              </div>
            );
          })}
          {eraserAt && (
            <div
              className="pointer-events-none absolute rounded-full border-2 border-dashed border-ink/70"
              style={{ left: eraserAt.x - (G.ERASER_WIDTH * v.s) / 2, top: eraserAt.y - (G.ERASER_WIDTH * v.s) / 2, width: G.ERASER_WIDTH * v.s, height: G.ERASER_WIDTH * v.s }}
            />
          )}
          {undoClear && (
            <div className="absolute inset-x-0 bottom-3 flex justify-center" onPointerDown={(e) => e.stopPropagation()}>
              <button
                onClick={() => {
                  doUndo();
                  setUndoClear(null);
                }}
                className="flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2.5 text-[14px] font-bold shadow-lg"
                data-theme="dark"
              >
                <Undo2 className="size-4" aria-hidden /> Undo clear
              </button>
            </div>
          )}
          {penSeen && <p className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 rounded-full bg-surface/90 px-3 py-1 text-[12px] text-muted">Stylus draws · finger moves players</p>}
        </div>

        {/* Tools: along the bottom upright, down the side when sideways */}
        <div className="relative flex shrink-0 items-center justify-between gap-1 border-t border-line px-2 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] landscape:flex-col landscape:justify-center landscape:border-t-0 landscape:border-l landscape:px-1.5 landscape:py-2" role="toolbar" aria-label="Drawing tools">
          {toolBtn('pen', 'Draw', Pencil)}
          {toolBtn('eraser', 'Eraser', Eraser)}
          {toolBtn('move', 'Move players', Hand)}
          <button
            onClick={() => setPalette(!palette)}
            aria-expanded={palette}
            aria-label={`Colour and line: ${COLOUR_NAMES[colour]}, ${thick ? 'thick' : 'thin'}`}
            className="grid size-12 place-items-center rounded-lg hover:bg-white/10"
          >
            <span className="grid size-7 place-items-center rounded-full border-2 border-white/30" style={{ background: INK.dark[colour] }}>
              <span className="rounded-full bg-black/40" style={{ width: thick ? 12 : 6, height: thick ? 12 : 6 }} />
            </span>
          </button>
          <button onClick={doUndo} disabled={!canUndo} className="grid size-12 place-items-center rounded-lg text-ink hover:bg-white/10 disabled:opacity-30" aria-label="Undo">
            <Undo2 className="size-6" aria-hidden />
          </button>
          <button onClick={doRedo} disabled={!canRedo} className="grid size-12 place-items-center rounded-lg text-ink hover:bg-white/10 disabled:opacity-30" aria-label="Redo">
            <Redo2 className="size-6" aria-hidden />
          </button>
          <button
            onClick={() => clear(false)}
            disabled={!board.strokes.length}
            className="flex h-12 items-center gap-1.5 rounded-lg bg-brand-gradient px-3 text-[13px] font-bold tracking-wide text-brand-ink uppercase disabled:bg-none disabled:bg-sunken disabled:text-muted landscape:w-12 landscape:justify-center landscape:px-0"
            aria-label="Quick erase: clear the drawing, keep the players"
            title="Clear the drawing (players stay)"
          >
            <Trash2 className="size-5" aria-hidden />
            <span className="landscape:hidden">Clear</span>
          </button>

          {palette && (
            <div
              className="absolute right-2 bottom-[calc(100%+6px)] z-10 rounded-xl border border-line bg-surface p-3 shadow-xl landscape:top-1/2 landscape:right-[calc(100%+6px)] landscape:bottom-auto landscape:-translate-y-1/2"
              role="dialog"
              aria-label="Colour and line"
            >
              <div className="flex gap-2">
                {(Object.keys(COLOUR_NAMES) as G.InkColour[]).map((c) => (
                  <button
                    key={c}
                    onClick={() => {
                      setColour(c);
                      if (tool !== 'pen') setTool('pen');
                    }}
                    aria-label={COLOUR_NAMES[c]}
                    aria-pressed={colour === c}
                    className={`grid size-11 place-items-center rounded-full ${colour === c ? 'ring-2 ring-blue ring-offset-2 ring-offset-surface' : ''}`}
                    style={{ background: INK.dark[c] }}
                  >
                    {colour === c && <Check className="size-5 text-[#0B0B14]" aria-hidden />}
                  </button>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[false, true].map((t) => (
                  <button
                    key={String(t)}
                    onClick={() => setThick(t)}
                    aria-pressed={thick === t}
                    className={`flex h-10 items-center justify-center gap-2 rounded-lg text-[13px] font-bold uppercase ${thick === t ? 'bg-blue text-brand-ink' : 'bg-sunken text-muted'}`}
                  >
                    <span className="rounded-full bg-current" style={{ width: 22, height: t ? 6 : 2 }} />
                    {t ? 'Thick' : 'Thin'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {menu && (
        <div className="fixed inset-0 z-20" onClick={() => setMenu(false)}>
          <div
            className="absolute top-12 right-2 w-[min(280px,calc(100vw-16px))] rounded-xl border border-line bg-surface p-2 shadow-xl"
            role="menu"
            aria-label="Board options"
            onClick={(e) => e.stopPropagation()}
          >
            {renaming ? (
              <RenameBoard
                name={board.name}
                onDone={(name) => {
                  if (name) setGame((g) => ({ ...g, boards: g.boards.map((b) => (b.id === board.id ? { ...b, name } : b)) }));
                  setRenaming(false);
                  setMenu(false);
                }}
              />
            ) : (
              <>
                <MenuButton onClick={() => setRenaming(true)}>Rename “{board.name}”</MenuButton>
                <MenuButton onClick={() => { clear(true); setMenu(false); }}>Clear everything (players back to the sidelines)</MenuButton>
                <MenuButton onClick={() => { setGame((g) => ({ ...g, light: !g.light })); setMenu(false); }}>{game.light ? 'Dark court' : 'Light court (for bright gyms)'}</MenuButton>
                {game.boards.length < G.MAX_BOARDS && <MenuButton onClick={() => { addBoard(); setMenu(false); }}>New board</MenuButton>}
                <MenuButton tone="danger" onClick={() => { removeBoard(); setMenu(false); }}>
                  {game.boards.length === 1 ? 'Reset this board' : `Delete “${board.name}”`}
                </MenuButton>
                <p className="px-3 pt-2 pb-1 text-[12px] text-muted">Boards are kept on this device until you clear them.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuButton({ onClick, children, tone }: { onClick: () => void; children: React.ReactNode; tone?: 'danger' }) {
  return (
    <button role="menuitem" onClick={onClick} className={`flex min-h-11 w-full items-center rounded-lg px-3 text-left text-[15px] hover:bg-sunken ${tone === 'danger' ? 'text-danger' : ''}`}>
      {children}
    </button>
  );
}

function RenameBoard({ name, onDone }: { name: string; onDone: (name: string | null) => void }) {
  const [value, setValue] = useState(name);
  return (
    <form
      className="flex items-center gap-2 p-1"
      onSubmit={(e) => {
        e.preventDefault();
        onDone(value.trim().slice(0, 24) || null);
      }}
    >
      <label className="sr-only" htmlFor="board-name">
        Board name
      </label>
      <input id="board-name" className="field-input" value={value} onChange={(e) => setValue(e.target.value)} maxLength={24} autoFocus placeholder="e.g. Press break" />
      <button type="submit" className="grid size-11 shrink-0 place-items-center rounded-lg bg-blue text-brand-ink" aria-label="Save name">
        <Check className="size-5" aria-hidden />
      </button>
      <button type="button" onClick={() => onDone(null)} className="grid size-11 shrink-0 place-items-center rounded-lg text-muted" aria-label="Cancel">
        <X className="size-5" aria-hidden />
      </button>
    </form>
  );
}
